import { zodResolver } from '@hookform/resolvers/zod';
import { PlusSquareIcon, TrashIcon } from '@phosphor-icons/react';
import { JSX, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Autocomplete, FileMimeTypes, FilePicker } from '@/modules/common';
import { FileObject } from '@/modules/common/types';

import { maxGcrFileSize } from '../constants';
import { GcrFormOptions } from '../types';
import { GcrFormSchema, GcrFormValues } from '../validations';

interface GcrFormProps {
  denseMode?: boolean;
  formClassName?: string;
  options: GcrFormOptions;
  defaultValues?: GcrFormValues;
  fileObject?: FileObject | null;
  onSubmit: (data: GcrFormValues) => Promise<void>;
  children: JSX.Element;
}

export const defaultFormValues: GcrFormValues = {
  name: '',
  colorId: '',
  printerId: '',
  filePublicUrl: '',
  combinations: [],
};

export function GcrForm({
  onSubmit,
  children,
  options,
  defaultValues,
  fileObject,
  formClassName = '',
  denseMode = false,
}: GcrFormProps) {
  const form = useForm<GcrFormValues>({
    resolver: zodResolver(GcrFormSchema),
    defaultValues: defaultFormValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'combinations',
  });

  const columnClassName = cn(
    'grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1',
    denseMode && 'grid-cols-2 lg:grid-cols-2 xl:grid-cols-2'
  );

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <Form {...form}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit(onSubmit)(event);
        }}
        className={formClassName}
      >
        {children}
        <div className="flex flex-col gap-10 max-w-6xl">
          <div className={columnClassName}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>GCR File Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter exact file name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className={columnClassName}>
            <div className="col-span-2">
              <FilePicker
                label="GCR File"
                name="filePublicUrl"
                accept={[FileMimeTypes.Gcr]}
                placeholder="Upload GCR File"
                tips={
                  <p className="text-sm text-zinc-500">
                    The file must be in
                    <span className="text-zinc-100">&nbsp;.gcr&nbsp;</span>
                    format and maximum size 100 MB.
                  </p>
                }
                maxFileSize={maxGcrFileSize}
                initialFileName={fileObject?.filename}
                initialFileSize={fileObject?.contentLength}
              />
            </div>
          </div>

          <div className={columnClassName}>
            <Autocomplete
              name="colorId"
              label="Model color"
              control={form.control}
              options={options.colors}
              placeholder="Select model color"
            />

            <Autocomplete
              name="printerId"
              label="Printer"
              control={form.control}
              options={options.printers}
              placeholder="Select printer"
            />
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Combinations</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                startIcon={<PlusSquareIcon />}
                onClick={() => append({ typeId: '', sizeId: '' })}
              >
                Add combination
              </Button>
            </div>

            {fields.length === 0 ? (
              <p className="text-sm text-zinc-500">
                No combinations added yet.
              </p>
            ) : (
              fields.map((fieldItem, index) => (
                <div
                  key={fieldItem.id}
                  className="flex items-end gap-6 max-w-3xl"
                >
                  <div className="flex-1">
                    <Autocomplete
                      name={`combinations.${index}.typeId`}
                      label="Model type"
                      control={form.control}
                      options={options.types}
                      placeholder="Select model type"
                    />
                  </div>

                  <div className="flex-1">
                    <Autocomplete
                      name={`combinations.${index}.sizeId`}
                      label="Model size"
                      control={form.control}
                      options={options.sizes}
                      placeholder="Select model size"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(index)}
                    data-testid={`remove-combination-${index}`}
                  >
                    <TrashIcon />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </form>
    </Form>
  );
}
